// ...existing code...
import React, { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import ControlledField from "./ControlledField";
import { formatCurrency } from "../../../../utils/format";

/*
  Reusable renderer for one 3-column cost row.
  Computes the right-hand total display when not provided by row.totalText.
*/
export default function ManualField({ row, index, fieldItem, control, FloatingInput, FloatingNumber, CustomSelect, /* formData removed */ cleanNumber, handleCurrencyBlur, onFormatted, getValues, setValue, salesPriceFormattedTick }) {
  // If rendered as part of a useFieldArray, callers will pass `index` and
  // `fieldItem`. In that case bind controllers to `closingCostMeta[index].*`.
  // Otherwise fall back to legacy dotted names in `row`.
  const isArrayMode = typeof index === 'number' && fieldItem;
  const basePath = isArrayMode ? `closingCostMeta.${index}` : null;
  const leftName = isArrayMode ? `${basePath}.label` : (row.left.name || "");
  const midName = isArrayMode ? `${basePath}.type` : (row.mid.name || "");
  const rightName = isArrayMode ? `${basePath}.value` : (row.right.name || "");

  // derive normalized base keys for matching (same normalization used for template items)
  const normalizeBase = (raw) => {
    const src = raw ?? "";
    // if dotted path provided, use last segment
    const last = (typeof src === 'string' && src.includes('.')) ? src.split('.').pop() : String(src);
    const derived = last.replace(/[-_ ]+(.)/g, (m, g1) => g1.toUpperCase()).replace(/[^a-zA-Z0-9]/g, '');
    if (!derived) return '';
    return derived.charAt(0).toLowerCase() + derived.slice(1);
  };

  const leftBase = normalizeBase(row?.left?.base ?? row?.left?.name ?? row?.left?.label);
  const midBase = normalizeBase(row?.mid?.base ?? row?.mid?.name ?? row?.mid?.label);
  const rightBase = normalizeBase(row?.right?.base ?? row?.right?.name ?? row?.right?.label);

  // prefer existing nested form value under closingCostMeta from RHF
  const currentValues = (typeof getValues === 'function') ? getValues() : {};
  const nested = currentValues?.closingCostMeta ?? {};
  // Defaults: prefer the array item values when in array mode, else fall back
  // to previous row defaults and nested values.
  const leftDefault = isArrayMode ? (fieldItem?.label ?? '') : (nested?.[leftBase] ?? (row.left.label ?? (row.left.placeholder ?? "")));
  const midDefault = isArrayMode ? (fieldItem?.type ?? (Array.isArray(row.mid.options) && row.mid.options.length ? row.mid.options[0] : row.mid.placeholder ?? "")) : (nested?.[midBase] ?? (Array.isArray(row.mid.options) && row.mid.options.length ? row.mid.options[0] : row.mid.placeholder ?? ""));
  const rightDefault = isArrayMode ? (fieldItem?.value ?? fieldItem?.rate ?? '') : (nested?.[rightBase] ?? (row.right.placeholder ?? ""));

  // watch type and rate/amount so we re-compute totals immediately when they change
  const [watchedType, watchedRight] = useWatch({ control, name: [midName, rightName] }) || [];
  // also watch possible loan fields so that totals depending on those
  // update immediately when those change elsewhere in the form
  const [watchedLoanAmount] = useWatch({ control, name: ["loanInfoMeta.adjusted"] }) || [];

  // compute total amount string when row.totalText not provided
  const computeAmountNumeric = () => {
    //console.log("Computing amount for row:", row);
    if (row.totalText) return NaN;

    // prefer live watched values, fall back to nested or defaults
    // prefer live watched values, fall back to array item values, nested or defaults
    const typeVal = (watchedType ?? (isArrayMode ? fieldItem?.type : undefined) ?? nested?.[midBase] ?? row.mid.placeholder ?? "").toString();
    const rawVal = (watchedRight ?? (isArrayMode ? (fieldItem?.value ?? fieldItem?.rate) : undefined) ?? nested?.[rightBase] ?? row.right.placeholder ?? "").toString();

    // sanitize numeric from right field (could be percent or amount)
    const cleaned = String(rawVal ?? "").replace(/[^0-9.-]+/g, "");
    const num = cleaned === "" ? NaN : Number(cleaned);

    // sales price and loan amount (read salesPrice from RHF when needed)
    const spRaw = ((typeof getValues === 'function') ? getValues('salesPrice') : '') ?? "";
    const salesPrice = Number(String(spRaw).replace(/[^0-9.-]+/g, "")) || 0;

    const laRaw = (watchedLoanAmount) ?? ((typeof getValues === 'function') ? (getValues('loanInfoMeta.adjusted') ?? '') : '') ?? "";
    const loanAmount = Number(String(laRaw).replace(/[^0-9.-]+/g, "")) || salesPrice;

    if (!Number.isFinite(num)) return NaN;

    // if type contains '% Sale Price' treat value as percent of salesPrice
    if (/%\s*sale|sale price|sale/i.test(typeVal)) {
      return (salesPrice * (num / 100)) || 0;
    }

    // if type contains '% Loan' treat value as percent of loan amount
    if (/%\s*loan|loan/i.test(typeVal)) {
      return (loanAmount * (num / 100)) || 0;
    }

    // Flat Fee or default: treat as flat amount
    return num;
  };

  const amountNumeric = computeAmountNumeric();
  const totalText = Number.isFinite(amountNumeric) ? `${formatCurrency(amountNumeric)}` : null;

  // When in array mode, persist computed per-row totals into the corresponding
  // closingCostMeta[index].total and closingCostMeta[index].Cash fields so
  // the array item stays in sync. This writes only the specific fields and
  // avoids replacing the whole array to prevent cross-row loops.
  // Recompute and persist when the row's own inputs (type or right field) change
  useEffect(() => {
    if (!isArrayMode || typeof setValue !== 'function' || typeof getValues !== 'function') return;
    try {
      const currAmount = computeAmountNumeric();
      if (!Number.isFinite(currAmount)) return;
      const currTotal = getValues(`${basePath}.total`);
      const newTotal = currAmount;
      if (Number(currTotal) !== Number(newTotal)) {
        setValue(`${basePath}.total`, newTotal, { shouldDirty: true, shouldTouch: true });
      }
    } catch (e) {
      // ignore errors
    }
  // only trigger when these local inputs change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedType, watchedRight, isArrayMode, basePath, setValue, getValues]);

  // Recompute and persist when salesPrice is formatted (blur). Parent increments
  // salesPriceFormattedTick on blur and it's passed here as a prop.
  useEffect(() => {
    if (!isArrayMode || typeof setValue !== 'function' || typeof getValues !== 'function') return;
    try {
      const currAmount = computeAmountNumeric();
      if (!Number.isFinite(currAmount)) return;
      const currTotal = getValues(`${basePath}.total`);
      const newTotal = currAmount;
      if (Number(currTotal) !== Number(newTotal)) {
        setValue(`${basePath}.total`, newTotal, { shouldDirty: true, shouldTouch: true });
      }
    } catch (e) {
      // ignore errors
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPriceFormattedTick, isArrayMode, basePath, setValue, getValues]);

  return (
    <div className="calc-frm-field">
      <div className="calc-frm-fld form-group clmn-a-width">
        <Controller
          name={leftName}
          control={control}
          defaultValue={leftDefault}
          render={({ field }) => (
            <FloatingInput {...field} placeholder={row.left.placeholder ?? ""}  type="text" onChange={(e) => field.onChange(e.target.value)} />
          )}
        />
      </div>

      <div className="calc-frm-fld form-group clmn-b-width">
        <Controller
          name={midName}
          control={control}
          defaultValue={midDefault}
          render={({ field }) => (
            <CustomSelect
              {...field}
              placeholder={row.mid.placeholder ?? ""}
              options={["% Loan", "% Sale Price", "Flat Fee"]}
            />
          )}
        />
      </div>

      <div className="calc-frm-fld form-group clmn-c-width">
        <ControlledField
          name={rightName}
          control={control}
          InputComponent={FloatingNumber}
          defaultValue={rightDefault}
          placeholder={row.right.placeholder ?? ""}
          label={row.right.label ?? ""}
          type="text"
          cleanNumber={cleanNumber}
          /* handleCurrencyBlur={handleCurrencyBlur}
          onFormatted={onFormatted} */
        />
      </div>

      <div className="calc-frm-fld form-group fld-txt-right clmn-d-width">{totalText}</div>
    </div>
  );
}